import parsePhoneNumber from 'libphonenumber-js';
import moment from 'moment';
import AWS from 'aws-sdk';
import { useLocation } from 'react-router-dom';
import axios, { AxiosRequestHeaders, AxiosResponse } from 'axios';
import * as XLSX from 'xlsx';
import { TAxiosBody, TAxiosParams } from './types';
import { brandUrls } from '../../../lib/vars';

moment.locale('es');

export const constructorName = (values: string[]) => values?.filter((x) => !!x)?.join(' ');

export const removeCurrency = (value: string | number): string =>
  value.toString().replace(/,/g, '');

export const toCurrency = (value: string | number, shouldRound = true): string => {
  const roundedValue = shouldRound ? Math.round(value as number) : value;
  const splitValue = roundedValue?.toString()?.split('.');

  if (splitValue?.length > 1) {
    return [
      removeCurrency(splitValue[0]?.toString())?.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.'),
      splitValue[1],
    ].join(',');
  }
  return removeCurrency(roundedValue?.toString())?.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
};

export const normalizePeriodicityValue = (value?: string | number | null) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/[\s-]+/g, '_');

export const isDateBasedDayTermCreditLine = (credit?: any) => {
  const periodicity = normalizePeriodicityValue(credit?.cPeriodicity ?? credit?.ctPeriodicity);
  const creditName = normalizePeriodicityValue(credit?.cName ?? credit?.ctName);
  const dayBasedPeriodicities = ['AVANCE_PRIMA', 'AVANCE_NOMINA', 'CDT', 'DIARIA'];

  // La periodicidad pactada en el crédito prevalece sobre el nombre histórico
  // de la línea (por ejemplo, una línea llamada DIARIA puede pactarse QUINCENAL).
  if (periodicity) return dayBasedPeriodicities.includes(periodicity);

  return (
    creditName.includes('AVANCE_PRIMA') ||
    creditName.includes('AVANCE_NOMINA') ||
    creditName.includes('PRIMA') ||
    creditName.includes('CDT') ||
    creditName.includes('DIARIA')
  );
};

export const getCreditTermDaysFromFirstFeeDate = (firstFeeDate?: string | Date | null) => {
  if (!firstFeeDate) return 0;

  const parsedFirstFeeDate = moment(
    firstFeeDate,
    ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'D-M-YYYY', 'D/M/YYYY'],
    true
  );
  const targetFirstFeeDate = (
    parsedFirstFeeDate.isValid() ? parsedFirstFeeDate : moment(firstFeeDate)
  ).startOf('day');

  if (!targetFirstFeeDate.isValid()) return 0;

  return Math.max(targetFirstFeeDate.diff(moment().startOf('day'), 'days'), 1);
};

export const getCreditTermDisplayValue = (credit?: any) => {
  if (!isDateBasedDayTermCreditLine(credit)) return Number(credit?.cMon || 0);

  if (normalizePeriodicityValue(credit?.cPeriodicity ?? credit?.ctPeriodicity) === 'DIARIA') {
    return Number(credit?.cMonVal || credit?.cDayCou || credit?.cMon || 0);
  }

  return (
    Number(credit?.cDayCou || credit?.cMonVal || 0) ||
    getCreditTermDaysFromFirstFeeDate(credit?.cFirFee) ||
    Number(credit?.cMon || 0)
  );
};

const PERIODICITY_PERIODS_PER_YEAR: Record<string, number> = {
  ANUAL: 1,
  SEMESTRAL: 2,
  CUATRIMESTRAL: 3,
  TRIMESTRAL: 4,
  BIMESTRAL: 6,
  MENSUAL: 12,
  QUINCENAL: 24,
  SEMANAL: 52,
};

export const getAnnualEffectiveRateFromPeriodicity = (
  periodicRate?: number,
  periodicity?: string | number | null,
  interestBase?: number
) => {
  const normalizedPeriodicity = normalizePeriodicityValue(periodicity || 'MENSUAL');
  const periodsPerYear =
    normalizedPeriodicity === 'DIARIA'
      ? Number(interestBase || 360)
      : PERIODICITY_PERIODS_PER_YEAR[normalizedPeriodicity] || PERIODICITY_PERIODS_PER_YEAR.MENSUAL;

  return ((1 + Number(periodicRate || 0) / 100) ** periodsPerYear - 1) * 100;
};

export const getMonthlyEquivalentRateFromPeriodicity = (
  periodicRate?: number,
  periodicity?: string | number | null,
  interestBase?: number
) => {
  const annualEffectiveRate = getAnnualEffectiveRateFromPeriodicity(
    periodicRate,
    periodicity,
    interestBase
  );

  return ((1 + annualEffectiveRate / 100) ** (1 / 12) - 1) * 100;
};

export const getPeriodicRateFromAnnualEffectiveRate = (
  annualEffectiveRate?: number,
  periodicity?: string | number | null,
  interestBase?: number
) => {
  const normalizedPeriodicity = normalizePeriodicityValue(periodicity || 'MENSUAL');
  const periodsPerYear =
    normalizedPeriodicity === 'DIARIA'
      ? Number(interestBase || 360)
      : PERIODICITY_PERIODS_PER_YEAR[normalizedPeriodicity] || PERIODICITY_PERIODS_PER_YEAR.MENSUAL;

  return ((1 + Number(annualEffectiveRate || 0) / 100) ** (1 / periodsPerYear) - 1) * 100;
};

export const getEquivalentPeriodicRate = ({
  periodicRate,
  fromPeriodicity,
  toPeriodicity,
  interestBase,
}: {
  periodicRate?: number;
  fromPeriodicity?: string | number | null;
  toPeriodicity?: string | number | null;
  interestBase?: number;
}) => {
  const annualEffectiveRate = getAnnualEffectiveRateFromPeriodicity(
    periodicRate,
    fromPeriodicity,
    interestBase
  );

  return getPeriodicRateFromAnnualEffectiveRate(annualEffectiveRate, toPeriodicity, interestBase);
};

const calculateAdjustableInterestByDays = (
  capital: number,
  annualEffectiveRate: number,
  fallbackPeriodicRate: number,
  days: number,
  periodicity?: string | number | null,
  interestBase?: number,
  simpleProrated = false
) => {
  let normalizedAnnualRate = Number(annualEffectiveRate || 0);
  const periodicRate = Number(fallbackPeriodicRate || 0);
  const normalizedPeriodicity = normalizePeriodicityValue(periodicity);

  if (normalizedAnnualRate > 1000) normalizedAnnualRate /= 10000;
  if (normalizedAnnualRate > 100) normalizedAnnualRate /= 100;

  let monthlyRate =
    normalizedPeriodicity && normalizedPeriodicity !== 'MENSUAL' && periodicRate > 0
      ? getMonthlyEquivalentRateFromPeriodicity(periodicRate, normalizedPeriodicity, interestBase)
      : periodicRate;

  if (monthlyRate <= 0 || (!normalizedPeriodicity && periodicRate > 5)) {
    monthlyRate =
      normalizedAnnualRate > 0
        ? ((1 + normalizedAnnualRate / 100) ** (1 / 12) - 1) * 100
        : periodicRate;
  }
  const monthFactor = Number(days || 0) / 30;

  if (simpleProrated) {
    return Number(capital || 0) * ((monthFactor * monthlyRate) / 100);
  }

  return Number(capital || 0) * ((1 + monthlyRate / 100) ** monthFactor - 1);
};

/**
 * Transforma una cantidad de números en formato dinero
 * @version 0.0.1
 * @param {number} value valor en números
 * @param {boolean} shouldRound indica si el valor debe redondearse antes de formatear
 * @return {string} valor formateado como dinero
 */
export const numberFormat = (value: string | number, shouldRound = true): string => {
  if (value) {
    return toCurrency(value || 0, shouldRound);
  }
  if (Number.isNaN(value as number)) return '';
  return '0';
};
/**
 * Validación de Email
 * @version 0.0.1
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
/**
 * Validación de solo Recibir letras
 * @version 0.0.1
 */
export const validateLetters = (value: string): boolean => {
  const lettersRegex = /^[A-Za-z\u00C0-\u024F\s]+$/;
  return lettersRegex.test(value);
};

/**
 *
 * @param {Object} data objeto a filtrar
 * @param {Array} filters array a comparar o claves del objeto a excluir
 * @return {Object} devuelve un objeto con los datos filtrados
 */
/* export const filterKeyObject = (data: any, filters: Array<string>) => {
  let values = {};

  Object.keys(data)?.forEach((elem: string) => {
    let coincidence = true;
    if (filters.find((x) => x === elem)) coincidence = false;
    if (coincidence) values = { ...values, [elem]: data[elem] };
  });

  return values;
}; */

/**
 * Filtra un objeto eliminando las claves especificadas
 * @version 0.0.1
 * @param {Record<string, any>} data objeto a filtrar
 * @param {string[]} filters array de claves del objeto a excluir
 * @return {Record<string, any>} devuelve un objeto con los datos filtrados
 */
export const filterKeyObject = (
  data: Record<string, any>,
  filters: string[]
): Record<string, any> => {
  const values: Record<string, any> = {};
  Object?.keys(data || {})?.forEach((elem: string) => {
    const value = data[elem];
    if (value !== null && value !== undefined && filters.indexOf(elem) === -1) {
      values[elem] = value;
    }
  });

  return values;
};

/*
 * Transforma un número en formato de teléfono
 * @version 0.0.1
 * @param {string} value valor en números
 * @return {string} nuevo formato en teléfono
 */
export const phoneFormat = (value: string) =>
  !!value && parsePhoneNumber(`${value}`, 'US')?.formatNational();

/**
 * Transforma una cadena de caracteres a todos mayúscula
 * @version 0.0.1
 * @param {string} value valor en números
 * @return {string} nuevo formato en teléfono
 */
export const upperCase = (value: string) => `${value || ''}`.toUpperCase();

export const normalizeCreditStateLabel = (value?: string | number | null) => {
  if (value === null || value === undefined) return '';
  if (Number(value) === 14) return 'Retirado de la pagadur\u00eda';

  const rawValue = `${value}`.trim();
  const normalizedValue = rawValue
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalizedValue.includes('retirado') && normalizedValue.includes('pagadur')) {
    return 'Retirado de la pagadur\u00eda';
  }

  return rawValue;
};

export const upperCaseCreditStateLabel = (value?: string | number | null) =>
  normalizeCreditStateLabel(value).toUpperCase();

export const normalizeToArray = <T = any>(data: T | T[] | null | undefined): T[] => {
  if (Array.isArray(data)) return data;
  if (data) return [data];
  return [];
};

/**
 * Transforma una fecha en ingles a formato espanol
 * @version 0.0.1
 * @param {string} value valor en números
 * @return {string} nuevo formato de fecha
 */
export const dateFormat = (value: string) => value && moment(value)?.format('DD-MM-YYYY');

/** busca los parámetros en la ruta
 * @return {Function} para poder llamar los parámetros
 */
export const useQueryParam = () => new URLSearchParams(useLocation().search);

/** busca los parámetros en la ruta
 * @return {Function} para poder llamar los parámetros
 */
export const getCreditStage = (state: number) => {
  let stage = '';
  switch (Number(state)) {
    case 0:
      stage = 'Rechazado';
      break;
    case 1:
      stage = 'Finalizado';
      break;
    case 2:
      stage = 'Solicitud';
      break;
    case 3:
      stage = 'Aprobación';
      break;
    case 4:
      stage = 'Documentos';
      break;
    case 5:
      stage = 'Espera de Desembolso';
      break;
    case 6:
      stage = 'Desembolsado';
      break;
    case 7:
      stage = 'Espera de Pagaduría';
      break;
    case 8:
      stage = 'Comité';
      break;
    case 9:
      stage = 'Supervisión';
      break;
    case 10:
      stage = 'Información adicional';
      break;
    case 12:
      stage = 'Desembolso parcial';
      break;
    case 14:
      stage = 'Retirado de la pagadur\u00eda';
      break;
    default:
      break;
  }
  return stage;
};
/**
 * function debounce
 * @param { function } func funciona que ejecuta el debounce
 * @return { function } función que limpia el intervalo
 */
export const debounce = (func: any) => {
  const interval = setTimeout(() => {
    func();
  }, 500);
  return interval && clearInterval(interval);
};

/**
 * Capitalizes first letters of words in string.
 * @param {string} str String to be modified
 * @param {boolean} lower Whether all other letters should be lowercase
 * @returns {string} retorna string
 */
export const capitalize = (str: string, lower = false) =>
  (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) => match.toUpperCase());

/**
 * Capitalizes first letters of words in string.
 * @param {string} str String to be modified
 * @returns {string} retorna string
 */
export const capitalizeTheFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

/**
 * Transforma un Tipo File a Base64 modo Async
 * @version 0.0.1
 * @async
 * @param {object} file tipo file
 * @return {string} base64
 */
export const toBase64 = (file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = (error) => reject(error);
  });

/**
 * transforma todo de base64 a arraybuffer
 * @version 0.0.1
 * @param {string} base64 valor
 * @return {array} array en bytes
 */
export const base64ToArrayBuffer = (base64: any) => {
  if (!base64) return false;
  const binaryString = window.atob(base64.substring(base64.indexOf('base64,') + 7, base64.length));
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString?.charCodeAt(i);
  }
  return bytes;
};

export const newBase64ToArrayBuffer = (base64: string) => {
  try {
    // Verifica si la entrada base64 es válida y no está vacía
    if (!base64) {
      throw new Error('La cadena Base64 está vacía o no es válida.');
    }

    // Elimina cualquier encabezado "base64," si está presente
    const base64Data = base64.includes('base64,') ? base64.split('base64,')[1] : base64;

    // Decodifica la cadena Base64 en un ArrayBuffer
    const binaryString = window.atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  } catch (error) {
    // Puedes elegir lanzar una excepción aquí si lo prefieres
    return null; // O devuelve null u otro valor que indique un error
  }
};

/**
 * Busca la extension del archivo
 * @version 0.0.1
 * @param {string} filename nombre del archivo con la extension
 * @return {string} nombre de la extension
 */
export const extFile = (filename: string) => {
  return /[.]/.exec(filename) ? /[^.]+$/?.exec(filename)?.[0] : undefined;
};

/**
 * Se conecta a Aws3
 * @version 0.0.1
 * @return {boolean} la conexión
 */
export const AWS3 = () =>
  new AWS.S3({
    accessKeyId: process.env.REACT_APP_ACCESS_KEY_AWS,
    secretAccessKey: process.env.REACT_APP_SECRET_KEY_AWS,
  });

/**
 * Busca un documento en S3
 * @param {String} Key variable de búsqueda
 * @return {String} base64 del documento
 */
export const getFileUrlS3 = async (Key: string) => {
  const client = brandUrls();
  const S3 = AWS3();
  const params = { Bucket: client.bt || 'pruebaawow', Prefix: Key };
  try {
    const response = await S3.listObjectsV2(params).promise();

    const objectKeys = response?.Contents?.map((obj: AWS.S3.Object) => obj.Key);
    const exists = objectKeys?.includes(Key) ?? false;
    if (!exists) return null;
    const res = await S3.getSignedUrlPromise('getObject', {
      Bucket: client.bt || 'pruebaawow',
      Key,
    });
    return res;
  } catch (error) {
    return undefined;
  }
};

/**
 * guarda un documento en S3
 * @param {String} Key variable de búsqueda
 * @param {Array} Body Array de buffer del documento
 * @return {String} respuesta del servidor
 */
export const putFileS3 = async (
  Key: string,
  Body: undefined | boolean | Uint8Array | ArrayBuffer
) => {
  const client = brandUrls();
  const S3 = AWS3();
  const res = await S3.putObject({
    Bucket: client.bt || 'pruebaawow',
    Key,
    Body,
  })
    .promise()
    .catch(() => undefined);
  return res;
};
export const arrayBufferTobase64 = (arrayBuffer) => {
  let base64 = '';
  const encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  const bytes = new Uint8Array(arrayBuffer);
  const byteLength = bytes?.byteLength;
  const byteRemainder = byteLength % 3;
  const mainLength = byteLength - byteRemainder;

  let a;
  let b;
  let c;
  let d;
  let chunk;

  // Main loop deals with bytes in chunks of 3
  for (let i = 0; i < mainLength; i += 3) {
    // Combine the three bytes into a single integer
    // eslint-disable-next-line no-bitwise
    chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];

    // Use bitmasks to extract 6-bit segments from the triplet
    // eslint-disable-next-line no-bitwise
    a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
    // eslint-disable-next-line no-bitwise
    b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
    // eslint-disable-next-line no-bitwise
    c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
    // eslint-disable-next-line no-bitwise
    d = chunk & 63; // 63       = 2^6 - 1

    // Convert the raw binary segments to the appropriate ASCII encoding
    base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
  }

  // Deal with the remaining bytes and padding
  if (byteRemainder === 1) {
    chunk = bytes[mainLength];

    // eslint-disable-next-line no-bitwise
    a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

    // Set the 4 least significant bits to zero
    // eslint-disable-next-line no-bitwise
    b = (chunk & 3) << 4; // 3   = 2^2 - 1

    base64 += `${encodings[a]}${encodings[b]}==`;
  } else if (byteRemainder === 2) {
    // eslint-disable-next-line no-bitwise
    chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];

    // eslint-disable-next-line no-bitwise
    a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
    // eslint-disable-next-line no-bitwise
    b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

    // Set the 2 least significant bits to zero
    // eslint-disable-next-line no-bitwise
    c = (chunk & 15) << 2; // 15    = 2^4 - 1

    base64 += `${encodings[a]}${encodings[b]}${encodings[c]}=`;
  }

  return base64;
};

export const blobToBase64 = async (blob) => {
  const base64Data = await new Promise((resolve) => {
    const reader: any = new FileReader();
    reader.onload = () => resolve(reader?.result?.split(',')[1]);
    reader.readAsDataURL(blob);
  });
  return base64Data;
};

/**
 * convertir blob a base64
 * @param {String} Key variable de búsqueda
 * @param {Array} Body Array de buffer del documento
 * @return {String} respuesta del servidor
 */
export const blobUrlToBase64 = async (blobUrl) => {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  const base64Data = await new Promise((resolve) => {
    const reader: any = new FileReader();
    reader.onload = () => resolve(reader?.result?.split(',')[1]);
    reader.readAsDataURL(blob);
  });
  return base64Data;
};

export const arrayBufferToBase64 = (arrayBuffer) => {
  // Convert ArrayBuffer to binary string
  let binaryString = '';
  const bytes = new Uint8Array(arrayBuffer);
  const length = bytes.byteLength;

  for (let i = 0; i < length; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }

  // Convert binary string to Base64
  const base64String = btoa(binaryString);

  return base64String;
};

export const constructorMoneyValue = (value: string) => {
  return `$ ${numberFormat(value)}`;
};

/**
 * Calcula el dígito de verificación (DV) de un NIT colombiano
 * @param {string | number} value NIT sin dígito de verificación
 * @return {number | string} el dígito de verificación
 */
export const calculateCheckDigit = (value: string | number): number | string => {
  let nit = `${value}`;
  const vpri = [undefined, 3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];

  nit = nit.replace(/\s/g, '');
  nit = nit.replace(/,/g, '');
  nit = nit.replace(/\./g, '');
  nit = nit.replace(/-/g, '');

  if (Number.isNaN(Number(nit))) return '';

  let x = 0;
  const z = nit.length;

  for (let i = 0; i < z; i++) {
    const y = parseInt(nit.substr(i, 1), 10);
    x += y * (vpri[z - i] || 0);
  }

  const y = x % 11;
  return y > 1 ? 11 - y : y;
};
export const diffInDays = (to: moment.Moment, from: moment.Moment) => {
  // Convertimos las fechas a objetos Date
  const dataTo = to.toDate();
  const dateFrom = from.toDate();

  // Convertimos las fechas a días
  const daysTo = dataTo.getFullYear() * 360 + dataTo.getMonth() * 30 + dataTo.getDate();
  const daysFrom = dateFrom.getFullYear() * 360 + dateFrom.getMonth() * 30 + dateFrom.getDate();

  // Calculamos la diferencia en días
  const difference = Math.abs(daysFrom - daysTo);

  return difference;
};

export const diffInDaysTwo = (to, from) => {
  // Convertimos las fechas a objetos Date
  const dataTo = new Date(to);
  const dateFrom = new Date(from);

  // Convertimos las fechas a días
  const daysTo = dataTo.getFullYear() * 360 + dataTo.getMonth() * 30 + dataTo.getDate();
  const daysFrom = dateFrom.getFullYear() * 360 + dateFrom.getMonth() * 30 + dateFrom.getDate();

  // Calculamos la diferencia en días
  const difference = Math.abs(daysFrom - daysTo);

  return difference;
};

export const numberFormatM = (param: number) => {
  let money = 0;
  let num: number | string = 0;
  let value = 0;
  let val = param;
  if (val >= 1000000000) {
    num = `${val}`.charAt(0);
    value = parseFloat(`${num}000`);
    money += value;
    val -= parseFloat(`${num}000000000`);
  }

  if (val >= 100000000) {
    num = `${val}`.charAt(0);
    value = parseFloat(`${num}00`);
    money += value;
    val -= parseFloat(`${num}00000000`);
  }

  if (val >= 10000000) {
    num = `${val}`.charAt(0);
    value = parseFloat(`${num}0`);
    money += value;
    val -= parseFloat(`${num}0000000`);
  }

  if (val >= 1000000) {
    num = `${val}`.charAt(0);
    value = parseFloat(`${num}`);
    money += value;
    val -= parseFloat(`${num}000000`);
  }
  if (val >= 100000) {
    num = `${val}`.charAt(0);
    value = parseFloat(`0.${num}`);
    money += value;
    val -= parseFloat(`${num}00000`);
  }
  if (val >= 10000) {
    num = `${val}`.charAt(0);
    value = parseFloat(`0.0${num}`);
    money += value;
    val -= parseFloat(`${num}0000`);
  }
  if (val >= 1000) {
    num = `${val}`.charAt(0);
    value = parseFloat(`0.00${num}`);
    money += value;
    val -= parseFloat(`${num}000`);
  }
  if (val >= 100) {
    num = `${val}`.charAt(0);
    value = parseFloat(`0.000${num}`);
    money += value;
    val -= parseFloat(`${num}00`);
  }
  if (val >= 10) {
    num = `${val}`.charAt(0);
    value = parseFloat(`0.0000${num}`);
    money += value;
    val -= parseFloat(`${num}0`);
  }
  if (val >= 1) {
    num = `${val}`.charAt(0);
    value = parseFloat(`0.00000${num}`);
    money += value;
    val -= parseFloat(`${num}`);
  }

  return money.toFixed(2);
};

/**
 * Función para consultar los datos de una API
 * @param {Any} method Método de búsqueda
 * @param {String} url Array de buffer del documento
 * @param {String} body Array de buffer del documento
 * @return {String} respuesta del servidor
 */
export const fetcher = async (
  method: string,
  url: string,
  header: AxiosRequestHeaders | Record<string, string | number | boolean>,
  body?: TAxiosBody,
  params?: TAxiosParams
): Promise<AxiosResponse> => {
  const requestBody = body?.request;
  const baseURL = brandUrls();
  const res: AxiosResponse = await axios({
    method,
    baseURL: `${baseURL?.bk}api/external/treasury${url}`,
    headers: header,
    params: params?.data,
    data: requestBody,
  });
  return res;
};
export const fetcherOss = async (
  method: string,
  url: string,
  header: AxiosRequestHeaders | Record<string, string | number | boolean>,
  body?: TAxiosBody,
  params?: TAxiosParams
): Promise<AxiosResponse> => {
  const requestBody = body?.request;
  const baseURL = brandUrls();
  const res: AxiosResponse = await axios({
    method,
    baseURL: `${baseURL?.ossado}${url}`,
    headers: header,
    params: params?.data,
    data: requestBody,
    timeout: 3600000,
  });
  return res;
};

export const exportToExcel = (data: any, filename: string) => {
  if (!data || data.length === 0) {
    return;
  }

  const workbook = XLSX.utils.book_new();

  // Obtener headers de las keys del primer objeto
  const headers = Object.keys(data[0]);

  // Crear array con headers y datos
  const rows: any[][] = [];
  rows.push(headers);

  // Agregar datos
  data.forEach((item: any) => {
    const row = headers.map((header) => item[header] ?? '');
    rows.push(row);
  });

  // Crear worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Calcular ancho óptimo de columnas
  const colWidths = headers.map((header) => {
    // Obtener el máximo entre el header y los valores de esa columna
    let maxWidth = header.length;

    data.forEach((item: any) => {
      const value = item[header];
      const valueLength = value !== null && value !== undefined ? String(value).length : 0;
      if (valueLength > maxWidth) {
        maxWidth = valueLength;
      }
    });

    // Agregar padding y limitar máximo
    return { wch: Math.min(maxWidth + 2, 50) };
  });

  worksheet['!cols'] = colWidths;

  const normalizeExcelHeader = (header: string) =>
    header
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const exactMoneyHeaders = [
    'valor cuota plena',
    'valor cuota esperada',
    'capital causado',
    'interes causado',
    'interes mora',
    'valor total recaudado',
    'valor total recaudado2',
    'total recaudo',
    'capital aplicado',
    'interes aplicado',
    'comision entidad recaudadora',
    'gastos cobranza',
    'seguro',
    'comisiones',
    'interes de mora',
    'otros',
    'saldos a favor',
    'saldo capital total antes aplicacion',
    'saldo capital total despues aplicacion',
    'saldo interes total antes aplicacion',
    'saldo interes total despues aplicacion',
  ];

  const isMoneyHeader = (header: string) => {
    const normalizedHeader = normalizeExcelHeader(header);

    return (
      exactMoneyHeaders.includes(normalizedHeader) ||
      normalizedHeader.includes('valor') ||
      normalizedHeader.includes('vr') ||
      normalizedHeader.includes('cuota') ||
      normalizedHeader.includes('prepago') ||
      normalizedHeader.includes('capital') ||
      normalizedHeader.includes('interes') ||
      normalizedHeader.includes('saldo') ||
      normalizedHeader.includes('comision') ||
      normalizedHeader.includes('gasto')
    );
  };

  const isNumericValue = (value: unknown) => typeof value === 'number' && Number.isFinite(value);

  // Aplicar formato de moneda a columnas monetarias.
  const moneyFormat = '"$" #,##0';
  const startDataRow = 1; // Fila donde empiezan los datos (0-indexed, despu?s del header)

  headers.forEach((header, colIdx) => {
    const isMoneyColumn = isMoneyHeader(header);

    if (isMoneyColumn) {
      data.forEach((_: any, rowIdx: number) => {
        const cellRef = XLSX.utils.encode_cell({ r: startDataRow + rowIdx, c: colIdx });
        if (worksheet[cellRef] && isNumericValue(worksheet[cellRef].v)) {
          worksheet[cellRef].z = moneyFormat;
        }
      });
    }
  });

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();

  URL.revokeObjectURL(url);
};

const CAJA_SOCIAL_DISBURSEMENTS_COLUMNS: Array<{
  key: string;
  width: number;
  type: 'text' | 'number';
  numFmt?: string;
  padZero?: boolean;
}> = [
  { key: 'FIJO 1', width: 1, type: 'text' },
  { key: 'FIJO 2', width: 2, type: 'text' },
  { key: 'Valor Neto', width: 10, type: 'number', numFmt: '0000000000' },
  { key: 'Centavos', width: 2, type: 'text', padZero: true },
  { key: 'Número de Cuenta', width: 17, type: 'text' },
  { key: 'Código del Banco', width: 9, type: 'text' },
  { key: 'Número de Identificación', width: 15, type: 'text' },
  { key: 'Nombre del Tercero (No usar caracteres especiales)', width: 22, type: 'text' },
  { key: 'FIJO 3', width: 2, type: 'text' },
  { key: 'Vacio', width: 13, type: 'text' },
  { key: 'Descripción', width: 10, type: 'text' },
  { key: 'Referencia', width: 30, type: 'text' },
  { key: 'Ubicar punto alineado a la derecha (.)', width: 28, type: 'text' },
];

// La columna "Ubicar punto alineado a la derecha (.)" siempre lleva un punto
// pegado al margen derecho de su ancho: se rellena a mano con espacios porque
// el free build de 'xlsx' no escribe alineación real de celda (ver plan de
// este reporte).
const CAJA_SOCIAL_LINE_END_KEY = 'Ubicar punto alineado a la derecha (.)';
const getCajaSocialLineEndValue = (width: number) => `${' '.repeat(Math.max(width - 1, 0))}.`;

export const exportFlatDisbursementsCajaSocialToExcel = (
  data: Array<Record<string, string | number | boolean | null | undefined>>,
  filename: string
) => {
  if (!data || data.length === 0) {
    return;
  }

  const workbook = XLSX.utils.book_new();

  const headerRow = CAJA_SOCIAL_DISBURSEMENTS_COLUMNS.map(({ key }) => key);

  const dataRows: any[][] = data.map((row) =>
    CAJA_SOCIAL_DISBURSEMENTS_COLUMNS.map(({ key, type, width, padZero }) => {
      if (key === CAJA_SOCIAL_LINE_END_KEY) {
        return getCajaSocialLineEndValue(width);
      }

      const value = row[key];

      if (padZero) {
        return String(Math.trunc(Number(value)) || 0).padStart(width, '0');
      }

      if (type === 'number') {
        return Number(value) || 0;
      }

      return value !== null && value !== undefined ? String(value) : '';
    })
  );

  const rows: any[][] = [headerRow, ...dataRows];

  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  worksheet['!cols'] = CAJA_SOCIAL_DISBURSEMENTS_COLUMNS.map(({ width }) => ({ wch: width }));

  CAJA_SOCIAL_DISBURSEMENTS_COLUMNS.forEach(({ numFmt }, colIdx) => {
    if (!numFmt) return;

    rows.forEach((_, rowIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
      if (worksheet[cellRef]) {
        worksheet[cellRef].z = numFmt;
      }
    });
  });

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();

  URL.revokeObjectURL(url);
};

export interface AmortizationExportData {
  productName: string;
  valReq: number;
  valCre: number;
  month: number;
  valCou: number;
  rateEM: number;
  rateEA: number;
  rateLabel?: string;
  fianza?: number;
  totalOrigin?: number;
  adjustableInterest?: number;
  ctModule?: number;
  ctType?: number;
  ctPeriodicity?: string;
  showAdjustableInterest?: boolean;
  repaymentPlan: Array<{
    date: string;
    startBalance: number;
    capital: number;
    interest: number;
    endBalance: number;
    otherAttr?: Array<Record<string, number>>;
  }>;
  fees?: Array<{ name: string; value: number }>;
}

export const exportToExcelAmortization = (data: AmortizationExportData, filename: string) => {
  const workbook = XLSX.utils.book_new();

  // Construir array de filas para el Excel
  const rows: any[][] = [];

  // Encabezado con información del producto (valores numéricos reales para alineación derecha)
  rows.push(['Línea de crédito', data.productName || '']);
  rows.push(['Valor desembolso', Math.round(data.valReq || 0)]);
  rows.push(['Valor crédito', Math.round(data.valCre || 0)]);
  rows.push(['Plazo (meses)', data.month || 0]);
  rows.push(['Valor cuota mensual', Math.round(data.valCou || 0)]);
  rows.push([data.rateLabel || 'Tasa interés EM', `${(data.rateEM || 0).toFixed(5)} %`]);
  rows.push(['Tasa interés EA', `${(data.rateEA || 0).toFixed(2)} %`]);

  if (data.fianza !== undefined && data.fianza > 0) {
    rows.push(['Fianza', Math.round(data.fianza)]);
  }

  if (data.totalOrigin !== undefined && data.totalOrigin > 0) {
    rows.push(['Total originación', Math.round(data.totalOrigin)]);
  }

  const adjustableInterest = Math.round(Number(data.adjustableInterest || 0));
  const shouldShowAdjustableInterest = data.showAdjustableInterest || adjustableInterest > 0;
  const periodicity = String(data.ctPeriodicity ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
  const isAdvanceInterest =
    periodicity !== 'DIARIA' &&
    data.ctModule === 1 &&
    (data.ctType === 2 || data.ctType === 3 || data.ctType === 4);

  // Fila vacía para separar
  if (shouldShowAdjustableInterest) {
    rows.push([
      isAdvanceInterest ? 'Intereses anticipados' : 'Intereses ajustables',
      adjustableInterest,
    ]);
  }

  rows.push([]);

  // Obtener columnas dinámicas de otherAttr
  const otherAttrKeys: string[] = [];
  if (data.repaymentPlan?.length && data.repaymentPlan[0]?.otherAttr?.length) {
    data.repaymentPlan[0].otherAttr.forEach((attr) => {
      Object.keys(attr).forEach((key) => {
        if (!otherAttrKeys.includes(key)) {
          otherAttrKeys.push(key);
        }
      });
    });
  }

  // Obtener nombres de fees
  const feeNames: string[] = data.fees?.map((fee) => fee.name) || [];

  // Encabezados de la tabla de amortización
  const tableHeaders = [
    'FECHA',
    'SALDO INICIAL',
    'CAPITAL',
    'INTERÉS',
    ...otherAttrKeys.map((k) => k.toUpperCase()),
    ...feeNames.map((f) => f.toUpperCase()),
    'SALDO FINAL',
    'CUOTA MENSUAL',
  ];
  rows.push(tableHeaders);

  // Guardar índice de inicio de la tabla de amortización
  const tableStartRow = rows.length;

  // Datos de la tabla de amortización (valores numéricos reales para alineación derecha)
  data.repaymentPlan?.forEach((plan) => {
    const otherAttrValues: number[] = otherAttrKeys.map((key) => {
      const found = plan.otherAttr?.find((attr) => attr[key] !== undefined);
      return found ? Math.round(found[key]) : 0;
    });

    const feeValues: number[] = feeNames.map((feeName) => {
      const fee = data.fees?.find((f) => f.name === feeName);
      return fee ? Math.round(fee.value) : 0;
    });

    rows.push([
      plan.date?.toUpperCase() || '', // Texto - se alinea a la izquierda
      Math.round(plan.startBalance), // Número - se alinea a la derecha
      Math.round(plan.capital), // Número - se alinea a la derecha
      Math.round(plan.interest), // Número - se alinea a la derecha
      ...otherAttrValues, // Números - se alinean a la derecha
      ...feeValues, // Números - se alinean a la derecha
      Math.round(plan.endBalance), // Número - se alinea a la derecha
      Math.round(data.valCou || 0), // Cuota mensual - se alinea a la derecha
    ]);
  });

  // Crear worksheet desde array de arrays
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Aplicar formato de moneda a las celdas numéricas de la tabla
  const numFormat = '"$" #,##0';
  const numCols = tableHeaders.length;

  // Formato para celdas de encabezado del crédito (columna B, filas 2-5 y posiblemente más)
  const headerMoneyRows = [1, 2, 4]; // Índices 0-based para: Valor desembolso, Valor crédito, Valor cuota
  headerMoneyRows.forEach((rowIdx) => {
    const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: 1 });
    if (worksheet[cellRef] && typeof worksheet[cellRef].v === 'number') {
      worksheet[cellRef].z = numFormat;
    }
  });

  // Si hay fianza (fila después de Tasa EA)
  let fianzaRow = 7;
  if (data.fianza !== undefined && data.fianza > 0) {
    const cellRef = XLSX.utils.encode_cell({ r: fianzaRow, c: 1 });
    if (worksheet[cellRef] && typeof worksheet[cellRef].v === 'number') {
      worksheet[cellRef].z = numFormat;
    }
    fianzaRow++;
  }

  // Si hay total originación
  if (data.totalOrigin !== undefined && data.totalOrigin > 0) {
    const cellRef = XLSX.utils.encode_cell({ r: fianzaRow, c: 1 });
    if (worksheet[cellRef] && typeof worksheet[cellRef].v === 'number') {
      worksheet[cellRef].z = numFormat;
    }
    fianzaRow++;
  }

  if (shouldShowAdjustableInterest) {
    const cellRef = XLSX.utils.encode_cell({ r: fianzaRow, c: 1 });
    if (worksheet[cellRef] && typeof worksheet[cellRef].v === 'number') {
      worksheet[cellRef].z = numFormat;
    }
  }

  // Formato para celdas de la tabla de amortización (todas las columnas numéricas excepto FECHA)
  data.repaymentPlan?.forEach((_, planIdx) => {
    const rowIdx = tableStartRow + planIdx;
    // Columnas numéricas: desde la 1 (SALDO INICIAL) hasta el final
    for (let colIdx = 1; colIdx < numCols; colIdx++) {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
      if (worksheet[cellRef] && typeof worksheet[cellRef].v === 'number') {
        worksheet[cellRef].z = numFormat;
      }
    }
  });

  // Ajustar ancho de columnas
  const colWidths = [
    { wch: 20 }, // FECHA
    { wch: 18 }, // SALDO INICIAL
    { wch: 15 }, // CAPITAL
    { wch: 15 }, // INTERÉS
    ...otherAttrKeys.map(() => ({ wch: 15 })),
    ...feeNames.map(() => ({ wch: 15 })),
    { wch: 18 }, // SALDO FINAL
    { wch: 18 }, // CUOTA MENSUAL
  ];
  worksheet['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Plan de Amortización');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportToTxt = (data: any, filename: string) => {
  const formatObject = (obj: any) =>
    Object.entries(obj || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');

  let content = '';

  if (typeof data === 'string') {
    content = data;
  } else if (Array.isArray(data)) {
    content = data.map((obj: any) => formatObject(obj)).join('\n');
  } else if (data && typeof data === 'object') {
    content = formatObject(data);
  }

  const blob = new Blob([content], {
    type: 'text/plain;charset=windows-1252',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = /\.[a-z0-9]+$/i.test(filename) ? filename : `${filename}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

const normalizePeriodicityText = (value?: any) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

const isFortnightCreditLine = (values?: any) =>
  normalizePeriodicityText(values?.cPeriodicity ?? values?.ctPeriodicity) === 'QUINCENAL' ||
  normalizePeriodicityText(values?.cName ?? values?.ctName).includes('QUINCEN');

const isShortPeriodicityCreditLine = (values?: any) =>
  ['DIARIA', 'SEMANAL', 'QUINCENAL'].includes(
    normalizePeriodicityText(values?.cPeriodicity ?? values?.ctPeriodicity)
  );

const addCurrentDayWhenChargingInterest = (days: number) => (days > 0 ? days + 1 : 0);

const getShortPeriodicityAdjustableDays = (
  credit: any,
  baseDate: moment.Moment,
  targetDate: moment.Moment
) => {
  const periodicity = normalizePeriodicityText(credit?.cPeriodicity ?? credit?.ctPeriodicity);
  const daysUntilFirstFee = Math.max(targetDate.diff(baseDate, 'days'), 0);
  const baseDaysByPeriodicity: Record<string, number> = {
    DIARIA: 0,
    SEMANAL: 6,
    QUINCENAL: 15,
  };
  const daysToCharge = daysUntilFirstFee - (baseDaysByPeriodicity[periodicity] ?? 0);

  if (periodicity === 'DIARIA' || periodicity === 'SEMANAL') {
    return Math.max(daysToCharge, 0);
  }

  if (periodicity === 'QUINCENAL') {
    if (daysUntilFirstFee < baseDaysByPeriodicity.QUINCENAL) return 0;

    return Math.max(daysToCharge + 1, 0);
  }

  return addCurrentDayWhenChargingInterest(Math.max(daysToCharge, 0));
};

const isDailyAdvanceCreditLine = (values?: any) => {
  const periodicity = normalizePeriodicityText(values?.cPeriodicity ?? values?.ctPeriodicity);
  const moduleValue = Number(values?.cModule ?? values?.ctModule);
  const typeValue = Number(values?.cType ?? values?.ctType);

  if (periodicity === 'DIARIA') return false;
  if (['AVANCE_PRIMA', 'AVANCE_NOMINA', 'CDT'].includes(periodicity)) return true;

  return (
    moduleValue === 1 &&
    (typeValue === 2 || typeValue === 3 || typeValue === 4) &&
    !isFortnightCreditLine(values)
  );
};

const getCommercialPeriodicityAdjustableDays = (
  credit: any,
  baseDate: moment.Moment,
  targetDate: moment.Moment
) => {
  const periodicity = normalizePeriodicityText(credit?.cPeriodicity ?? credit?.ctPeriodicity);
  const commercialDaysByPeriodicity: Record<string, number> = {
    MENSUAL: 30,
    BIMESTRAL: 60,
    TRIMESTRAL: 90,
    CUATRIMESTRAL: 120,
    SEMESTRAL: 180,
    ANUAL: 360,
  };
  const monthsByPeriodicity: Record<string, number> = {
    MENSUAL: 1,
    BIMESTRAL: 2,
    TRIMESTRAL: 3,
    CUATRIMESTRAL: 4,
    SEMESTRAL: 6,
    ANUAL: 12,
  };
  const commercialMonths = monthsByPeriodicity[periodicity];

  if (!commercialDaysByPeriodicity[periodicity] || !commercialMonths) return null;

  const hasFeeAdjust = Number(credit?.cFeeAdjust ?? credit?.ctFeeAdjust ?? 0) === 1;
  const shouldUseCurrentMonthAdjustment = [
    'BIMESTRAL',
    'TRIMESTRAL',
    'CUATRIMESTRAL',
    'SEMESTRAL',
    'ANUAL',
  ].includes(periodicity);
  const normalFirstFeeDate = baseDate.clone().add(commercialMonths, 'month').startOf('day');
  const isFirstFeeInCurrentMonth = targetDate.isSame(baseDate, 'month');

  if (isFirstFeeInCurrentMonth) {
    if (periodicity === 'MENSUAL') return 0;

    return addCurrentDayWhenChargingInterest(Math.max(targetDate.diff(baseDate, 'days'), 0));
  }

  if (targetDate.isSameOrBefore(normalFirstFeeDate, 'day')) {
    return 0;
  }

  const currentMonthDays = Math.max(
    baseDate.clone().endOf('month').startOf('day').diff(baseDate, 'days') + 1,
    0
  );
  const normalFirstFeeEndOfMonth = normalFirstFeeDate.clone().endOf('month').startOf('day');

  if (periodicity === 'MENSUAL' && targetDate.isSameOrAfter(normalFirstFeeEndOfMonth, 'day')) {
    if (!targetDate.isSame(targetDate.clone().endOf('month'), 'day')) {
      return currentMonthDays + targetDate.diff(normalFirstFeeEndOfMonth, 'days');
    }

    const fullMonthsBetween = Math.max(
      targetDate
        .clone()
        .startOf('month')
        .diff(baseDate.clone().add(1, 'month').startOf('month'), 'months'),
      0
    );

    return currentMonthDays + fullMonthsBetween * 30;
  }

  if (
    hasFeeAdjust &&
    shouldUseCurrentMonthAdjustment &&
    targetDate.isSameOrAfter(normalFirstFeeEndOfMonth, 'day')
  ) {
    if (!targetDate.isSame(targetDate.clone().endOf('month'), 'day')) {
      return currentMonthDays + targetDate.diff(normalFirstFeeEndOfMonth, 'days');
    }

    const fullMonthsAfterNormalFeeMonth = Math.max(
      targetDate
        .clone()
        .startOf('month')
        .diff(normalFirstFeeEndOfMonth.clone().add(1, 'day').startOf('month'), 'months'),
      0
    );

    return currentMonthDays + fullMonthsAfterNormalFeeMonth * 30;
  }

  return Math.max(targetDate.diff(normalFirstFeeDate, 'days'), 0);
};

/**
 * Calcula interés ajustable solo cuando el crédito está marcado como ajustable.
 * Regla general: en el mes del desembolso cobra días calendario reales desde esa fecha hasta
 * la primera cuota. Si la primera cuota cae en la fecha normal del mes siguiente,
 * no cobra; si se pasa de esa fecha normal, cobra solo esa diferencia.
 * Si el tipo tiene ajuste personalizado, usa aqInterestDays.
 * Fórmula general: Capital * (1 + tasaMensual/100)^(días / 30) - Capital
 * @return {number} Retorna valor del interés ajustable
 */
export const getVatAdj = (
  cValCre: number,
  cRatVal: number,
  credit?: any,
  adjustmentQuotas?: any[],
  annualEffectiveRate?: number
) => {
  const disbursementDate = moment(credit?.ecDatInc || new Date()).startOf('day');
  const adjustableAnnualRate = Number(
    annualEffectiveRate ??
      credit?.currentUsuryAnnualRate ??
      credit?.cRate ??
      credit?.ctRate ??
      cRatVal ??
      0
  );

  // Si el estado del crédito es 6 o 1, usar el valor guardado.
  if (credit?.cState === 6 || credit?.cState === 1) {
    return credit?.cValAdj || 0;
  }

  if (credit?.cAdjustable !== 1) {
    return 0;
  }

  if (Number(credit?.cFeeAdjustmentPersonalized) === 1) {
    const expenseDay = Number(moment(credit?.ecDatInc || new Date()).format('DD'));
    const matchingQuota = adjustmentQuotas
      ?.filter((q: any) => Number(q?.aqState ?? 1) === 1)
      ?.find(
        (q: any) => expenseDay >= Number(q?.aqFrom || 0) && expenseDay <= Number(q?.aqUntil || 0)
      );
    const interestDays = Number(matchingQuota?.aqInterestDays || 0);

    if (isDailyAdvanceCreditLine(credit)) {
      const rate = adjustableAnnualRate / 100;
      const attributesAmount = (credit?.creditAttributeCres || []).reduce(
        (acc: number, attribute: any) => acc + Number(attribute?.cacAmount || 0),
        0
      );
      const interestBase =
        Number(credit?.cInterestBase || credit?.creditType?.ctInterestBase) || 360;
      const netDisbursement =
        Number(cValCre || 0) / (1 + rate) ** (interestDays / interestBase) - attributesAmount;
      const result = Math.max(Math.round(Number(credit?.cReqVal || 0) - netDisbursement), 0) || 0;
      return result;
    }

    if (Number(credit?.cModule) === 1) {
      const vatAdj = calculateAdjustableInterestByDays(
        cValCre,
        adjustableAnnualRate,
        0,
        interestDays,
        credit?.cPeriodicity ?? credit?.ctPeriodicity,
        credit?.cInterestBase ?? credit?.ctInterestBase ?? credit?.creditType?.ctInterestBase,
        false
      );
      const result = Math.max(Math.round(vatAdj), 0) || 0;
      return result;
    }

    const vatAdj = calculateAdjustableInterestByDays(
      cValCre,
      adjustableAnnualRate,
      0,
      interestDays,
      credit?.cPeriodicity ?? credit?.ctPeriodicity,
      credit?.cInterestBase ?? credit?.ctInterestBase ?? credit?.creditType?.ctInterestBase,
      false
    );
    const result = Math.max(Math.round(vatAdj), 0) || 0;
    return result;
  }

  const isDailyAdvance = isDailyAdvanceCreditLine(credit);
  if (isDailyAdvance) {
    const baseFirstFeeDate = disbursementDate;
    const targetFirstFeeDate = moment(credit?.cFirFee).startOf('day');
    const dayCou = Math.max(targetFirstFeeDate.diff(baseFirstFeeDate, 'days'), 0);
    const rate = adjustableAnnualRate / 100;
    const attributesAmount = (credit?.creditAttributeCres || []).reduce(
      (acc: number, attribute: any) => acc + Number(attribute?.cacAmount || 0),
      0
    );
    const interestBase = Number(credit?.cInterestBase || credit?.creditType?.ctInterestBase) || 360;

    if (!dayCou || !Number(cValCre || 0)) return 0;

    const netDisbursement =
      Number(cValCre || 0) / (1 + rate) ** (dayCou / interestBase) - attributesAmount;
    const vatAdj = Number(credit?.cReqVal || 0) - netDisbursement;
    return Math.max(Math.round(vatAdj), 0) || 0;
  }

  // Base de cálculo: fecha efectiva del egreso; usa hoy solo cuando el flujo no la proporciona.
  const baseFirstFeeDate = disbursementDate;
  const targetFirstFeeDate = moment(credit?.cFirFee).startOf('day');
  const originalFirstFeeDate = credit?.cOriginalFirFee
    ? moment(credit?.cOriginalFirFee).startOf('day')
    : null;

  if (!baseFirstFeeDate.isValid() || !targetFirstFeeDate.isValid()) {
    return 0;
  }

  const periodicity = normalizePeriodicityText(credit?.cPeriodicity ?? credit?.ctPeriodicity);
  const isFirstFeeInCurrentMonth = targetFirstFeeDate.isSame(baseFirstFeeDate, 'month');

  if (!periodicity && isFirstFeeInCurrentMonth) {
    return 0;
  }

  const hasFeeAdjust = Number(credit?.cFeeAdjust ?? credit?.ctFeeAdjust ?? 0) === 1;

  if (!hasFeeAdjust) {
    const daysToCharge = (() => {
      if (isShortPeriodicityCreditLine(credit)) {
        return getShortPeriodicityAdjustableDays(credit, baseFirstFeeDate, targetFirstFeeDate);
      }

      const commercialDaysToCharge = getCommercialPeriodicityAdjustableDays(
        credit,
        baseFirstFeeDate,
        targetFirstFeeDate
      );

      if (commercialDaysToCharge !== null) {
        return commercialDaysToCharge;
      }

      return Math.max(
        addCurrentDayWhenChargingInterest(
          baseFirstFeeDate.clone().endOf('month').startOf('day').diff(baseFirstFeeDate, 'days')
        ),
        0
      );
    })();

    if (daysToCharge <= 0) {
      return 0;
    }

    const vatAdj = calculateAdjustableInterestByDays(
      cValCre,
      adjustableAnnualRate,
      cRatVal,
      daysToCharge,
      credit?.cPeriodicity ?? credit?.ctPeriodicity,
      credit?.cInterestBase ?? credit?.ctInterestBase ?? credit?.creditType?.ctInterestBase,
      false
    );
    return Math.round(vatAdj) || 0;
  }

  const commercialDaysToCharge = getCommercialPeriodicityAdjustableDays(
    credit,
    baseFirstFeeDate,
    targetFirstFeeDate
  );

  if (commercialDaysToCharge !== null) {
    if (commercialDaysToCharge <= 0) {
      return 0;
    }

    const vatAdj = calculateAdjustableInterestByDays(
      cValCre,
      adjustableAnnualRate,
      cRatVal,
      commercialDaysToCharge,
      credit?.cPeriodicity ?? credit?.ctPeriodicity,
      credit?.cInterestBase ?? credit?.ctInterestBase ?? credit?.creditType?.ctInterestBase,
      false
    );
    return Math.round(vatAdj) || 0;
  }

  const wasFirstFeeManuallyChanged =
    originalFirstFeeDate?.isValid() && !targetFirstFeeDate.isSame(originalFirstFeeDate, 'day');
  const extraDays = wasFirstFeeManuallyChanged
    ? addCurrentDayWhenChargingInterest(
        Math.max(targetFirstFeeDate.diff(baseFirstFeeDate, 'days'), 0)
      )
    : Math.max(
        addCurrentDayWhenChargingInterest(
          baseFirstFeeDate.clone().endOf('month').startOf('day').diff(baseFirstFeeDate, 'days')
        ),
        0
      );

  if (extraDays <= 0) {
    return 0;
  }
  const vatAdj = calculateAdjustableInterestByDays(
    cValCre,
    adjustableAnnualRate,
    cRatVal,
    extraDays,
    credit?.cPeriodicity ?? credit?.ctPeriodicity,
    credit?.cInterestBase ?? credit?.ctInterestBase ?? credit?.creditType?.ctInterestBase,
    false
  );
  return Math.round(vatAdj) || 0;
};

/**
 * Calcula el método del crédito
 * @return {string} Retorna el método
 */
export const getMethod = (cReTanking: number, cOptPay: number) => {
  if (cReTanking === 1 || cOptPay === 1) {
    if (cReTanking === 1) {
      return 'RETANQUEO';
    }
    return 'REFINANCIACIÓN';
  }
  return 'NORMAL';
};

const getFirstFeeDateFromPeriod = (period?: string) => {
  if (!period) return '';

  const normalizedPeriod = period.toString().trim();
  const parsedFullDate = moment(
    normalizedPeriod,
    [moment.ISO_8601, 'DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', 'D-M-YYYY'],
    'es',
    true
  );
  if (parsedFullDate.isValid()) return parsedFullDate.format('YYYY-MM-DD');

  const parsedMonth = moment(normalizedPeriod, ['MMM YYYY', 'MMMM YYYY'], 'es', true);
  if (parsedMonth.isValid()) return parsedMonth.endOf('month').format('YYYY-MM-DD');

  const [monthText = '', yearText = ''] = normalizedPeriod.toLowerCase().split(/\s+/);
  const monthKey = monthText.replace(/\./g, '').slice(0, 3);
  const monthMap: Record<string, number> = {
    ene: 0,
    feb: 1,
    mar: 2,
    abr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    ago: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dic: 11,
    jan: 0,
    apr: 3,
    aug: 7,
    dec: 11,
  };
  const month = monthMap[monthKey];
  const year = Number(yearText);

  if (month === undefined || !year) return '';

  return moment({ year, month }).endOf('month').format('YYYY-MM-DD');
};

const getAdvanceInterestDays = (calculator: any, values?: any) => {
  const calculatorTerm = Number(calculator?.realMonth ?? calculator?.month ?? 0);
  const periodicity = normalizePeriodicityText(values?.cPeriodicity ?? values?.ctPeriodicity);

  if (periodicity === 'AVANCE_PRIMA' && calculatorTerm > 0) return calculatorTerm;

  const formTerm = Number(values?.mDefVal ?? values?.month ?? values?.cMonVal ?? values?.cMon ?? 0);
  if (formTerm > 0) return formTerm;

  if (calculatorTerm > 0) return calculatorTerm;

  return 0;
};

export const getCalculatedAdjustableInterest = (
  calculator: any,
  values?: any,
  currentUsuryAnnualRate?: number,
  forceAdjustable?: boolean,
  adjustmentQuotas?: any[]
) => {
  const isDailyAdvanceLine = isDailyAdvanceCreditLine(values);
  const explicitValue = Number(
    calculator?.valAdj ??
      calculator?.vatAdj ??
      calculator?.cValAdj ??
      calculator?.adjustableInterest ??
      0
  );

  let firstFeeDate = values?.cFirFee;
  if (isDailyAdvanceLine) {
    const days = getAdvanceInterestDays(calculator, values);
    firstFeeDate = moment().add(days, 'days').format('YYYY-MM-DD');
  } else if (calculator?.repaymentPlan?.[0]?.date) {
    firstFeeDate = getFirstFeeDateFromPeriod(calculator?.repaymentPlan?.[0]?.date) || firstFeeDate;
  }

  const creditLike = {
    ...values,
    cAdjustable:
      forceAdjustable === true ? 1 : Number(values?.cAdjustable ?? values?.ctAdjustable ?? 0),
    cFeeAdjustmentPersonalized: Number(
      values?.cFeeAdjustmentPersonalized ?? values?.ctFeeAdjustmentPersonalized ?? 0
    ),
    cFeeAdjust: Number(values?.cFeeAdjust ?? values?.ctFeeAdjust ?? 0),
    cFirFee: firstFeeDate,
    cModule: values?.cModule ?? values?.ctModule,
    cType: values?.cType ?? values?.ctType,
    cPeriodicity: values?.cPeriodicity ?? values?.ctPeriodicity,
    cInterestBase: values?.cInterestBase ?? values?.ctInterestBase,
    cReqVal: calculator?.valReq ?? values?.cReqVal,
    cValCre: calculator?.valCre ?? values?.cValCre,
    cRatVal: calculator?.rate ?? values?.cRatVal ?? values?.ctRatVal,
    cValAdj: values?.cValAdj ?? explicitValue,
    cRate: values?.cRate ?? values?.ctRate,
    ctRate: values?.ctRate,
    creditAttributeCres: (calculator?.credits || []).map((attribute: any) => ({
      cacAmount: Number(attribute?.value || 0),
    })),
    currentUsuryAnnualRate,
  };

  if (Number(creditLike?.cAdjustable) !== 1 || !firstFeeDate) {
    return 0;
  }

  const value = getVatAdj(
    Number(creditLike?.cValCre || 0),
    Number(creditLike?.cRatVal || 0),
    creditLike,
    adjustmentQuotas,
    currentUsuryAnnualRate
  );

  return value > 1 ? Math.round(value) : 0;
};

export const getDisbursementAfterAdjustableInterest = (calculator: any, adjustableInterest = 0) =>
  Math.max(Math.round(Number(calculator?.valReq || 0) - Number(adjustableInterest || 0)), 0);

export const getDisplayedDisbursementValue = (
  calculator: any,
  adjustableInterest: number,
  values?: any
) => {
  const isDailyAdvanceLine = isDailyAdvanceCreditLine(values);

  if (isDailyAdvanceLine) {
    return Math.max(
      Math.round(Number(calculator?.valReq || 0) - Number(adjustableInterest || 0)),
      0
    );
  }

  return getDisbursementAfterAdjustableInterest(calculator, adjustableInterest);
};

export const getBase64UrlPdf = (base64String: string) => {
  const base64Data = base64String.split(',')[1];

  // Convierte la cadena Base64 en un arreglo de bytes
  const byteCharacters = window?.atob(base64Data);

  // Convierte los bytes en un arreglo de tipo unsigned 8 bits
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  // Crea un Blob a partir del arreglo de bytes
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });

  // Genera una URL Blob a partir del Blob
  const blobUrl = URL.createObjectURL(blob);

  return blobUrl;
};

/**
 * Funcion que devuelve los anos hasta el actual desde uno inicial
 * @param initialYear Ano de inicio
 * @returns years
 */
export const getYears = (initialYear: number) => {
  const currentYear = new Date()?.getFullYear();
  const yearsCount = currentYear - initialYear + 1;
  const years = new Array(yearsCount);

  for (let i = 0; i < yearsCount; i++) {
    const year = initialYear + i;
    years[i] = { value: year, label: year?.toString() };
  }

  return years;
};

export const fromLettersToNumber = (numString: number | string) => {
  const numAsString = String(numString);
  const numWithoutDots = numAsString.replace(/\./g, '');
  const num = parseFloat(numWithoutDots);
  return num;
};

export const diffFechas = (fechaInicio, fechaFin = new Date()) => {
  if (!fechaInicio) return 0;

  const start = new Date(fechaInicio);
  const end = new Date(fechaFin);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }

  // Si la fecha inicio es mayor que hoy
  if (start >= end) {
    return 0;
  }

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  const days = end.getDate() - start.getDate();

  // Días incompletos NO suman mes
  if (days < 0) {
    months--;
  }

  // Ajuste meses negativos
  if (months < 0) {
    years--;
    months += 12;
  }

  const totalMeses = years * 12 + months;

  return Math.max(totalMeses, 0);
};

export const ultimoDiaDelMes = (mes, anio) => {
  let siguienteMes;
  let siguienteAnio;

  if (mes === 12) {
    siguienteMes = 1;
    siguienteAnio = anio + 1;
  } else {
    siguienteMes = mes + 1;
    siguienteAnio = anio;
  }

  const fechaInicioMesSiguiente = moment(`${siguienteAnio}-${siguienteMes}-01`, 'YYYY-MM-DD');
  const fechaUltimoDia = fechaInicioMesSiguiente.subtract(1, 'days');

  return fechaUltimoDia.format('DD-MM-YYYY');
};

/**
 * Extrae los nombres de archivos de documentos personales del payroll
 * para poder descargarlos desde S3.
 * Incluye: CreditPayrollDocument, CreditPayrollCompanyOtherDocuments,
 * y PayrollDocumentsToRequest (getCreditPayrollDocumentToRequest).
 * @param payrollData - Resultado de getCreditPayrollCompanyById
 * @param documentsToRequest - Resultado de getCreditPayrollDocumentToRequest (opcional)
 * @returns Array de { fileName, route } listos para descargar
 */
export type TDownloadablePayrollDocument = {
  fileName: string;
  route: string;
  displayName?: string;
  downloadFolder?: string;
};

const PAYROLL_DOCUMENT_LABELS: Record<string, string> = {
  cpdPerDoc: 'Documento personal',
  cpdFroDoc: 'Documento frontal',
  cpdReaDoc: 'Documento reverso',
  cpdSerDoc: 'Documento de servicios',
  cpdPayRemDoc: 'Desprendible de pago',
  cpdBanDoc: 'Documento bancario',
  cpdConDoc: 'Contrato',
  cpdComActDoc: 'Certificado actividad comercial',
  cpdBudRecDoc: 'Recibo predial',
  cpdBudCerDoc: 'Certificado predial',
  cpdRut: 'RUT',
  cpdComChamber: 'Cámara de comercio',
  cpdInstitutionPic: 'Foto institucional',
  cpdWorkCertificate: 'Certificado laboral',
  cpdVoucher: 'Comprobante',
  cpdBankStatement: 'Extracto bancario',
  cpdFinancialStatements: 'Estados financieros',
  cpdIncomeTaxReturn: 'Declaración de renta',
  cpdBoardDirectors: 'Junta directiva',
  cpdTradeBalance: 'Balance comercial',
  cpdRealEstateRegistration: 'Matrícula inmobiliaria',
  cpdVehicleOwnershipCard: 'Tarjeta de propiedad vehicular',
  cpdCommercialReference: 'Referencia comercial',
  cpdBankReference: 'Referencia bancaria',
  cpdIncomeWithholdings: 'Retenciones',
  cpdCadastreAndValuation: 'Catastro y avalúo',
  cpdCommercialAppraisal: 'Avalúo comercial',
  cpdLeaseCertificate: 'Certificado de arrendamiento',
  cpdLibertyAndTraditionCertificate: 'Certificado libertad y tradición',
};

export const extractPayrollDocFiles = (
  payrollData: any,
  documentsToRequest?: any[]
): TDownloadablePayrollDocument[] => {
  if (!payrollData) return [];
  const pcId = payrollData?.pcId;
  if (!pcId) return [];

  const docs: TDownloadablePayrollDocument[] = [];
  const addedFileNames = new Set<string>();

  const addDoc = (fName: string, displayName?: string) => {
    if (fName && !addedFileNames.has(fName)) {
      addedFileNames.add(fName);
      docs.push({
        fileName: fName,
        route: `payroll/${pcId}/documents/${fName}`,
        displayName,
        downloadFolder: 'documentos_personales',
      });
    }
  };

  // 1. Documentos del CreditPayrollDocument (campos individuales cpd*)
  const docFields = payrollData.creditPayrollDocument;
  if (docFields) {
    const excludeKeys = ['cpdId', 'cpcId', '__typename', 'cpdState'];
    Object.keys(docFields).forEach((key) => {
      if (!excludeKeys.includes(key) && docFields[key]) {
        addDoc(docFields[key], PAYROLL_DOCUMENT_LABELS[key] || 'Documento personal');
      }
    });
  }

  // 2. Otros documentos (CreditPayrollCompanyOtherDocuments)
  const otherDocs = payrollData.creditPayrollCompanyOtherDocuments;
  if (otherDocs?.length) {
    otherDocs
      .filter((d: any) => d?.cpcodState === 1 && d?.cpcodFileName)
      .forEach((d: any) => {
        addDoc(d.cpcodFileName, 'Otro documento del cliente');
      });
  }

  // 3. Documentos requeridos por tipo de crédito (getCreditPayrollDocumentToRequest)
  if (documentsToRequest?.length) {
    documentsToRequest
      .filter((d: any) => d?.fileName)
      .forEach((d: any) => {
        addDoc(d.fileName, d?.dtrName || 'Documento solicitado');
      });
  }

  return docs;
};

const CREDIT_NOTIFICATION_PHASE_SEPARATOR = '__p__';

const cleanCreditNotificationPhaseName = (phaseName?: string) =>
  (phaseName || '')
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9\u00c0-\u017f\s._-]/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 80);

const buildCreditNotificationTitle = (phaseName?: string) => {
  const cleanPhaseName = cleanCreditNotificationPhaseName(phaseName)
    .replace(/_/g, ' ')
    .replace(/^notificacion\s+/i, '')
    .trim();

  return `Notificacion ${cleanPhaseName || 'Credito'}`;
};

const getCreditNotificationDateTime = (date = new Date()) => {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(
    date.getHours()
  )}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

export const buildCreditNotificationFileName = (fileName: string, phaseName?: string): string => {
  const cleanFileName = (fileName || 'documento.pdf')
    .replace(/[^a-zA-Z0-9\u00c0-\u017f._-]/g, '_')
    .replace(/_+/g, '_');
  const extension = cleanFileName.match(/\.[^.]+$/)?.[0] || '';
  const timestamp = getCreditNotificationDateTime();
  const baseName = buildCreditNotificationTitle(phaseName);
  const maxBaseLength = Math.max(1, 50 - timestamp.length - extension.length - 1);

  return `${baseName.replace(/\s+/g, '_').slice(0, maxBaseLength)}_${timestamp}${extension}`;
};

const getCreditNotificationTitleFromFileName = (fileName: string) => {
  const timestamp = fileName?.match(/\d{8}_\d{6}/)?.[0] || '';
  if (!timestamp) return '';

  const baseName = fileName
    .replace(/\.[^.]+$/, '')
    .replace(new RegExp(`_?${timestamp}$`), '')
    .replace(/_/g, ' ')
    .trim();

  return /^notificacion\s+/i.test(baseName) ? buildCreditNotificationTitle(baseName) : '';
};

const getCreditNotificationDocumentInfo = (fileName: string, notificationName?: string) => {
  const storedPhaseSeparator = [CREDIT_NOTIFICATION_PHASE_SEPARATOR, '_p_'].find((separator) =>
    fileName?.includes(separator)
  );
  const [phaseName, storedName] = storedPhaseSeparator
    ? fileName.split(storedPhaseSeparator)
    : ['', fileName];

  const originalFileName = storedName?.replace(/^\d+_/, '') || fileName;
  const extension = originalFileName.match(/\.[^.]+$/)?.[0] || '';
  const timestamp = originalFileName.match(/\d{8}_\d{6}/)?.[0] || '';
  const resolvedPhaseName =
    (phaseName && buildCreditNotificationTitle(phaseName)) ||
    getCreditNotificationTitleFromFileName(originalFileName) ||
    (notificationName && buildCreditNotificationTitle(notificationName)) ||
    'Notificacion de credito';
  const displayFileName = timestamp
    ? `${resolvedPhaseName} ${timestamp}${extension}`
    : originalFileName;

  return {
    phaseName: resolvedPhaseName,
    displayFileName:
      storedPhaseSeparator && !timestamp ? `${resolvedPhaseName}${extension}` : displayFileName,
  };
};

export const buildCreditNotificationDocuments = (creditReports: any[], cId?: string) =>
  (creditReports || [])
    ?.flatMap((report: any) =>
      (report?.creditReportDocuments || [])
        ?.filter((documentItem: any) => documentItem?.crdState !== 0 && documentItem?.crdFileName)
        ?.map((documentItem: any) => {
          const documentInfo = getCreditNotificationDocumentInfo(
            documentItem?.crdFileName,
            report?.notification?.nName
          );

          return {
            cdId: `notification-${documentItem?.crdId || report?.crId}-${documentItem?.crdFileName}`,
            cId,
            cdGroup: documentInfo.phaseName,
            cdCategory: 'OTROS',
            cdName: documentItem?.crdFileName,
            cdRoute: `credits/${cId}/report/${documentItem?.crdFileName}`,
            displayFileName: documentInfo.displayFileName,
            isNotificationDocument: true,
          };
        })
    )
    ?.filter((documentItem: any) => documentItem?.cdName) || [];

export const buildDebtCapacityCreditDocument = (debtCapacityInfo: any, cId?: string) => {
  if (!debtCapacityInfo?.cdciPdfRoute) return null;

  return {
    cdId: `debt-capacity-${debtCapacityInfo?.cdciId || cId || debtCapacityInfo.cdciPdfRoute}`,
    cId,
    cdGroup: 'Capacidad de Descuento',
    cdCategory: 'OTROS',
    cdName: 'Capacidad de Descuento.pdf',
    cdRoute: debtCapacityInfo.cdciPdfRoute,
  };
};

export const buildPayrollCreditDocuments = (payrollData: any, documentsToRequest?: any[]) =>
  extractPayrollDocFiles(payrollData, documentsToRequest).map((documentItem, index) => ({
    cdId: `payroll-${index}-${documentItem.fileName}`,
    cdGroup: documentItem.displayName || 'Documento personal',
    cdCategory: 'SOLICITUD',
    cdName: documentItem.fileName,
    cdRoute: documentItem.route,
    displayFileName: documentItem.fileName,
    isPayrollDocument: true,
    downloadFolder: documentItem.downloadFolder,
  }));

export const buildAllCreditDownloadDocuments = ({
  creditDocuments = [],
  creditReports = [],
  debtCapacityInfo = null,
  payrollData = null,
  documentsToRequest = [],
  cId,
}: {
  creditDocuments?: any[];
  creditReports?: any[];
  debtCapacityInfo?: any;
  payrollData?: any;
  documentsToRequest?: any[];
  cId?: string;
}) => {
  const debtCapacityDocument = buildDebtCapacityCreditDocument(debtCapacityInfo, cId);

  return [
    ...(creditDocuments || []),
    ...buildCreditNotificationDocuments(creditReports || [], cId),
    ...(debtCapacityDocument ? [debtCapacityDocument] : []),
    ...buildPayrollCreditDocuments(payrollData, documentsToRequest),
  ].filter((documentItem: any) => documentItem?.cdName);
};

export const getUniqueCreditDocuments = (documents: any[] = []) =>
  documents?.reduce((prev: any[], current: any) => {
    const exists = prev.find(
      (item: any) =>
        (current?.cdId && item?.cdId === current?.cdId) ||
        (item?.cdGroup === current?.cdGroup && item?.cdName === current?.cdName) ||
        (item?.cdName === current?.cdName && item?.cdRoute === current?.cdRoute)
    );
    return exists ? prev : [...prev, current];
  }, []);

export const getCreditDocumentDisplayName = (documentItem: any) => {
  if (documentItem?.displayFileName) return documentItem.displayFileName;

  const isNotificationDocument =
    documentItem?.isNotificationDocument ||
    (documentItem?.cdCategory === 'OTROS' && documentItem?.cdRoute?.includes('/report/'));

  if (isNotificationDocument && documentItem?.cdGroup) {
    const extension = documentItem?.cdName?.match(/\.[^.]+$/)?.[0] || '';
    const timestamp = documentItem?.cdName?.match(/\d{8}_\d{6}/)?.[0] || '';
    const title = /^notificacion\s+/i.test(documentItem.cdGroup)
      ? documentItem.cdGroup
      : `Notificacion ${documentItem.cdGroup}`;
    return `${title}${timestamp ? ` ${timestamp}` : ''}${extension}`;
  }

  return documentItem?.cdName;
};

export const getCreditDocumentDownloadRoute = (documentItem: any, cId?: string) => {
  if (documentItem?.cdRoute) return documentItem.cdRoute;
  return `credits/${cId}/${documentItem?.cdName}`;
};
